import {
  Blocks,
  Briefcase,
  GraduationCap,
  LifeBuoy,
  type LucideIcon,
  Megaphone,
  Newspaper,
  Rocket,
  Scale,
  ShieldCheck,
  ShoppingBag,
  Terminal,
  Users,
} from 'lucide-react'
import type { useTranslations } from 'next-intl'
import { FREE_TOOLS, type FreeToolId } from '@/lib/free-tools/catalog'

import type { Locale } from '@/lib/i18n/config'
import type { Messages } from '@/lib/i18n/messages'
import { localizePath } from '@/lib/i18n/routing'
import type {
  DropdownNavItem,
  DropdownNavLink,
  DropdownNavVisual,
} from './dropdown-navigation'

/** `useTranslations('marketingNav')`. */
export type NavTranslator = ReturnType<typeof useTranslations<'marketingNav'>>

type NavMessages = Messages['marketingNav']

/** A catalog entry with a label and a description: one menu row. */
type NavLinkKey = {
  [K in keyof NavMessages]: NavMessages[K] extends {
    label: string
    description: string
  }
    ? K
    : never
}[keyof NavMessages]

type NavLinkEntry = {
  key: NavLinkKey
  href: string
  icon: LucideIcon
  /** Opens in a new tab (docs, other sites). */
  external?: boolean
}

const LIFECYCLE_VISUAL: DropdownNavVisual = {
  kind: 'stack',
  images: [
    { src: '/landing/home/lifecycle/welcome.webp', width: 520, height: 800 },
    {
      src: '/landing/home/lifecycle/account-ready.webp',
      width: 520,
      height: 800,
    },
    {
      src: '/landing/home/lifecycle/first-campaign.webp',
      width: 520,
      height: 800,
    },
  ],
  backdrop: '/landing/home/lifecycle/sky.webp',
}

const TEMPLATES_VISUAL: DropdownNavVisual = {
  kind: 'fan',
  backdrop: '/landing/home/templates/sky.webp',
  images: [
    { src: '/landing/home/templates/opal.webp', width: 520, height: 800 },
    { src: '/landing/home/templates/tiffany.webp', width: 520, height: 800 },
    { src: '/landing/home/templates/sonos.webp', width: 520, height: 800 },
  ],
}

/** Who Brew is for; each lands on the page that serves them best. */
const SOLUTIONS: ReadonlyArray<NavLinkEntry> = [
  { key: 'marketers', href: '/marketers', icon: Megaphone },
  { key: 'startups', href: '/startups', icon: Rocket },
  { key: 'agencies', href: '/agencies', icon: Briefcase },
  { key: 'ecommerce', href: '/ecommerce', icon: ShoppingBag },
]

/** Each free tool's Resources row: its catalog entry and icon. */
const FREE_TOOL_KEYS: Record<FreeToolId, NavLinkKey> = {
  'email-audit': 'emailAudit',
}
const FREE_TOOL_ICON: Record<FreeToolId, LucideIcon> = {
  'email-audit': ShieldCheck,
}

const RESOURCES: ReadonlyArray<NavLinkEntry> = [
  { key: 'integrations', href: '/browse/integrations', icon: Blocks },
  { key: 'blog', href: '/blog', icon: Newspaper },
  { key: 'customers', href: '/customers', icon: Users },
  ...FREE_TOOLS.map(
    (tool): NavLinkEntry => ({
      key: FREE_TOOL_KEYS[tool.id],
      href: tool.path,
      icon: FREE_TOOL_ICON[tool.id],
    })
  ),
  { key: 'compare', href: '/compare', icon: Scale },
  {
    key: 'api',
    href: 'https://docs.brew.new/api-reference/api/api-introduction',
    icon: Terminal,
    external: true,
  },
  { key: 'university', href: '/university', icon: GraduationCap },
  { key: 'support', href: '/support', icon: LifeBuoy },
]

/** The bar's right-hand text link, beside the sign-up CTA. */
export const NAV_MCP_HREF = '/mcp'

/**
 * The public navbar's entries in the reader's language: Pricing, the
 * Solutions menu (who Brew is for: marketers, startups, agencies,
 * ecommerce), the Resources menu, and Docs. MCP and the sign-up CTA sit on
 * the bar's right. Every link points at a live page, in the reader's locale.
 */
export function buildNavItems(
  t: NavTranslator,
  locale: Locale
): ReadonlyArray<DropdownNavItem> {
  const link = ({
    key,
    href,
    icon,
    external,
  }: NavLinkEntry): DropdownNavLink => ({
    label: t(`${key}.label`),
    description: t(`${key}.description`),
    href: external ? href : localizePath(locale, href),
    icon,
    ...(external ? { external } : {}),
  })
  return [
    { label: t('pricing'), href: localizePath(locale, '/pricing') },
    {
      label: t('solutions'),
      menu: {
        feature: {
          title: t('lifecycle.title'),
          description: t('lifecycle.description'),
          href: localizePath(locale, '/browse/flows'),
          visual: LIFECYCLE_VISUAL,
        },
        groups: [{ items: SOLUTIONS.map(link) }],
      },
    },
    {
      label: t('resources'),
      menu: {
        feature: {
          title: t('templates.title'),
          description: t('templates.description'),
          href: localizePath(locale, '/browse/templates'),
          visual: TEMPLATES_VISUAL,
        },
        groups: [{ layout: 'columns', items: RESOURCES.map(link) }],
      },
    },
    { label: t('docs'), href: 'https://docs.brew.new/', external: true },
  ]
}
